import { useEffect, useRef } from 'react'
import { RoomEvent } from 'livekit-client'
import { getSocket } from '../../services/socket'
import { useLivekitStore } from '../../stores/livekitStore'
import { useRemoteControlStore } from '../../stores/remoteControlStore'
import { makeDataReceivedHandler } from '../../services/remoteControl/channel'
import { shouldInject } from '../../services/remoteControl/injectGate'
import { dialog } from '../../services/dialog'

interface RemoteControlBridge {
  arm: (a: { sessionId: string; sourceId: string }) => Promise<{ ok: boolean; reason?: string }>
  disarm: () => Promise<unknown>
  inject: (p: unknown) => void
  showBanner: (i: { name: string }) => Promise<unknown>
  hideBanner: () => Promise<unknown>
  onBannerStop: (cb: () => void) => () => void
  preflightPermissions: () => Promise<{ ok: boolean }>
}
interface IncomingPayload {
  sessionId: string; fromUserId: string; fromName: string
  controllerIdentity: string; channelId: string; serverId: string
}

// The desktop preload exposes window.electronAPI.remoteControl. Absent in a plain
// browser (a browser controllee cannot be driven — handled gracefully).
function desktopRC(): RemoteControlBridge | undefined {
  return window.electronAPI?.remoteControl
}

// The local screen-share track's capture source id ("screen:<n>:0") identifies the
// shared display. Prefer the id stashed by the desktop source picker; fall back to
// the track settings deviceId. Empty ⇒ arm() rejects (full-monitor-only).
function getLocalScreenSourceId(): string {
  const stashed = (window as unknown as { __rx2goScreenSourceId?: string }).__rx2goScreenSourceId
  return stashed || ''
}

export default function RemoteControlController() {
  const session = useRemoteControlStore((s) => s.session)
  const sessionRef = useRef(session)
  sessionRef.current = session

  // 1. Socket signaling: incoming request (consent), decision (controller side), stop.
  useEffect(() => {
    const socket = getSocket()
    const onIncoming = async (p: IncomingPayload) => {
      const rc = desktopRC()
      if (!rc) return // browser controllee can't be driven; ignore silently
      useRemoteControlStore.getState().onIncoming(p)
      const perm = await rc.preflightPermissions().catch(() => ({ ok: true }))
      const ok = await dialog.confirm({
        title: 'Allow remote control?',
        message:
          `${p.fromName} wants to control your screen. They'll be able to move your mouse, type, and paste from their clipboard.` +
          (perm.ok ? '' : '\n\nGrant Accessibility & Screen-Recording permission first for this to work.'),
        confirmLabel: 'Allow',
      })
      if (!ok) { useRemoteControlStore.getState().deny(); return }
      useRemoteControlStore.getState().approve()
    }
    const onDecision = (p: { sessionId: string; accepted: boolean }) =>
      useRemoteControlStore.getState().onDecision(p)
    const onStop = (p: { sessionId: string; reason: string }) => {
      if (sessionRef.current.sessionId === p.sessionId) useRemoteControlStore.getState().end(p.reason)
    }
    socket.on('remote-control:incoming', onIncoming)
    socket.on('remote-control:decision', onDecision)
    socket.on('remote-control:stop', onStop)
    return () => {
      socket.off('remote-control:incoming', onIncoming)
      socket.off('remote-control:decision', onDecision)
      socket.off('remote-control:stop', onStop)
    }
  }, [])

  // 2. While active as the controllee: arm injection, show banner, gate-and-inject packets.
  useEffect(() => {
    if (session.role !== 'controllee' || session.state !== 'active' || !session.sessionId) return
    const rc = desktopRC()
    const room = useLivekitStore.getState().room
    if (!rc || !room) return

    rc.arm({ sessionId: session.sessionId, sourceId: getLocalScreenSourceId() }).then((r) => {
      if (!r.ok) { useRemoteControlStore.getState().end('arm-failed:' + (r.reason || 'unknown')); return }
      void rc.showBanner({ name: session.peerName || 'Someone' })
    })

    const handler = makeDataReceivedHandler((senderIdentity, sid, packet) => {
      if (shouldInject(sessionRef.current, senderIdentity, sid)) rc.inject(packet)
    })
    room.on(RoomEvent.DataReceived, handler)
    const offBanner = rc.onBannerStop(() => useRemoteControlStore.getState().end('controllee-stop'))

    return () => {
      room.off(RoomEvent.DataReceived, handler)
      offBanner()
    }
  }, [session.role, session.state, session.sessionId, session.peerName, session.controllerIdentity])

  // 3. On ended: disarm, hide banner, reset to idle.
  useEffect(() => {
    if (session.state !== 'ended') return
    const rc = desktopRC()
    void rc?.disarm()
    void rc?.hideBanner()
    const t = setTimeout(() => useRemoteControlStore.getState().reset(), 50)
    return () => clearTimeout(t)
  }, [session.state])

  return null
}
